import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config';
import { FALLBACK_PROFILE_IMAGE, PROFILE_DEFAULTS } from '../constants/profileDefaults';

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  onProfileUpdated: (updatedUser: any) => void;
}

const EditProfileModal: React.FC<EditProfileModalProps> = ({ isOpen, onClose, user, onProfileUpdated }) => {
  const [nickname, setNickname] = useState(user?.nickname || '');
  const [fotoPerfil, setFotoPerfil] = useState(user?.fotoPerfil || FALLBACK_PROFILE_IMAGE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setNickname(user?.nickname || '');
      setFotoPerfil(user?.fotoPerfil || FALLBACK_PROFILE_IMAGE);
      setError('');
    }
  }, [isOpen, user]);

  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.put(
        getApiUrl('/api/auth/profile'),
        { nickname, fotoPerfil },
        { withCredentials: true }
      );
      const updatedUser = { ...user, ...response.data };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      onProfileUpdated(updatedUser);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al actualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={onClose}>
      <div className="card-gaming max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-gaming text-gaming-purple text-lg tracking-wider">EDITAR PERFIL</h2>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white transition-colors text-2xl"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-3 py-2 rounded text-sm font-main">
              {error}
            </div>
          )}

          {/* Foto perfil */}
          <div className="flex flex-col items-center gap-3">
            <div className="w-24 h-24 rounded-full bg-white/10 border-2 border-gaming-purple overflow-hidden flex items-center justify-center">
              <img src={fotoPerfil || FALLBACK_PROFILE_IMAGE} alt="Perfil" className="w-[78%] h-[78%] object-contain" />
            </div>
            <p className="text-white/60 text-xs font-main text-center">Elige uno de los avatares por defecto</p>
            <div className="grid grid-cols-4 gap-3 w-full max-w-xs mx-auto">
              {PROFILE_DEFAULTS.map((avatar) => (
                <button
                  key={avatar}
                  type="button"
                  onClick={() => setFotoPerfil(avatar)}
                  className={`w-14 h-14 rounded-full overflow-hidden border-2 transition-colors ${
                    fotoPerfil === avatar
                      ? 'border-gaming-purple shadow-lg shadow-purple-500/30'
                      : 'border-white/10 hover:border-white/40'
                  }`}
                >
                  <div className="w-full h-full flex items-center justify-center bg-white/5 rounded-full">
                    <img src={avatar} alt="Avatar de perfil" className="w-[76%] h-[76%] object-contain" />
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Nickname */}
          <div>
            <label className="block font-main text-white/70 text-sm mb-2">Nickname</label>
            <input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-white font-main focus:outline-none focus:border-gaming-purple"
            />
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-lg border border-white/20 text-white/70 hover:border-white/40 transition-colors font-gaming text-sm"
            >
              CANCELAR
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex-1 btn-primary py-2 font-gaming text-sm tracking-wider disabled:opacity-50"
            >
              {loading ? 'Guardando...' : 'GUARDAR'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProfileModal;
